/* =====================================================================
   EL MANTENIMIENTO PROGRAMADO, del lado del cliente · Entrega B (20 sep 2026)
   =====================================================================
   Una sola fuente para la banda y para la pantalla: `GET /api/mantenimiento`
   (público, sin dato de nadie), leído cada cinco minutos y guardado en
   `localStorage` — porque cuando llegue el corte la API no va a contestar
   y la pantalla tiene que saber igual qué está pasando, cuánto lleva y
   cuánto falta.

   TODO-SIRVE: sin nada programado, `programado` es null y nadie pinta nada.

   El reloj del servidor manda: `ahora` viene en cada respuesta y se guarda
   el desfase con el del teléfono, para que «falta 1 h» no dependa de la
   hora que tenga puesta la tableta del almacén.
   ===================================================================== */
import { useCallback, useEffect, useState } from 'react';

const CLAVE = 'tego_mantenimiento';
const CLAVE_CERRADA = 'tego_mantenimiento_cerrada';
export const CADA_MS = 5 * 60 * 1000;
export const UNA_HORA_MS = 60 * 60 * 1000;
// Antes de la hora de inicio, la pantalla no salta aunque la API se caiga
// (sería otra cosa: sin conexión). Este margen absorbe un reinicio que
// empieza unos minutos antes de lo programado.
export const MARGEN_ANTES_MS = 3 * 60 * 1000;
// Y después del fin estimado se sigue enseñando (diciendo que pasó del
// estimado) hasta que la API vuelva o hasta este tope.
export const TOPE_DESPUES_MS = 2 * 60 * 60 * 1000;

export function leerGuardado() {
  try {
    const crudo = localStorage.getItem(CLAVE);
    return crudo ? JSON.parse(crudo) : null;
  } catch {
    return null;
  }
}

function guardar(d) {
  try {
    if (d && d.programado) localStorage.setItem(CLAVE, JSON.stringify(d));
    else localStorage.removeItem(CLAVE);
  } catch { /* modo privado */ }
}

export async function leerDelServidor() {
  const r = await fetch('/api/mantenimiento', { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const cuerpo = await r.json();
  const desfase = cuerpo.ahora ? new Date(cuerpo.ahora).getTime() - Date.now() : 0;
  const d = { programado: cuerpo.programado || null, desfase: Number.isFinite(desfase) ? desfase : 0, leido: Date.now() };
  guardar(d);
  return d;
}

/* Qué fase va: `lejos` (más de 1 h), `ultima_hora`, `en_curso`, `pasado`
   (del estimado), `terminado`, o null si no hay nada que decir. */
export function fase(programado, ahoraServidor) {
  if (!programado || !programado.inicio) return null;
  const inicio = new Date(programado.inicio).getTime();
  const fin = programado.fin_estimado ? new Date(programado.fin_estimado).getTime()
    : inicio + (Number(programado.duracion_min) || 10) * 60000;
  if (Number.isNaN(inicio) || Number.isNaN(fin)) return null;
  if (programado.terminado_en) return 'terminado';
  if (ahoraServidor < inicio - UNA_HORA_MS) return 'lejos';
  if (ahoraServidor < inicio) return 'ultima_hora';
  if (ahoraServidor <= fin) return 'en_curso';
  if (ahoraServidor <= fin + TOPE_DESPUES_MS) return 'pasado';
  return null;
}

export function estaCerrada(programado, f) {
  try {
    return localStorage.getItem(CLAVE_CERRADA) === `${programado.id}:${f}`;
  } catch {
    return false;
  }
}

export function cerrar(programado, f) {
  try { localStorage.setItem(CLAVE_CERRADA, `${programado.id}:${f}`); } catch { /* modo privado */ }
}

export function cuandoEnPalabras(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', hour: 'numeric', minute: '2-digit' });
}

/* El gancho: el programado vigente, la fase y el reloj del servidor, al día. */
export function useMantenimiento(cadaMs = CADA_MS) {
  const [dato, setDato] = useState(() => leerGuardado());
  const [tic, setTic] = useState(() => Date.now());
  useEffect(() => {
    let vivo = true;
    const leer = () => leerDelServidor().then((d) => { if (vivo) setDato(d); }).catch(() => { /* se queda lo guardado */ });
    leer();
    const t = setInterval(leer, cadaMs);
    const reloj = setInterval(() => setTic(Date.now()), 30000);
    return () => { vivo = false; clearInterval(t); clearInterval(reloj); };
  }, [cadaMs]);
  const releer = useCallback(() => leerDelServidor().then(setDato).catch(() => {}), []);
  const ahoraServidor = tic + ((dato && dato.desfase) || 0);
  const programado = dato ? dato.programado : null;
  return { programado, fase: fase(programado, ahoraServidor), ahoraServidor, releer };
}
